import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

type Props = {
  children: ReactNode;
  /** Optional fallback renderer; default = "Something went wrong + Reload". */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null };

/**
 * Top-level error boundary so a thrown render in any page doesn't blank out
 * the whole admin shell. Logs the error to the console (where the source-maps
 * land), then offers a Reload action.
 *
 * React 18 doesn't propagate render errors past boundaries in production
 * automatically — keep this around even if you add per-route boundaries later.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <Box p={4} display="flex" flexDirection="column" gap={2} alignItems="center">
        <Typography variant="h5" fontWeight={700}>
          Алдаа гарлаа
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 480, textAlign: "center" }}>
          {error.message || "Тодорхойгүй алдаа. Хуудсыг шинэчилнэ үү."}
        </Typography>
        <Box display="flex" gap={1.5}>
          <Button variant="outlined" onClick={this.reset}>
            Дахин оролдох
          </Button>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Хуудсыг шинэчлэх
          </Button>
        </Box>
      </Box>
    );
  }
}
